import { FC, ReactElement, useState } from "react";
import clsx from "clsx";
import { Dropdown } from "react-bootstrap";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { format, toZonedTime } from 'date-fns-tz';
import { CountryFlags } from "../../_metronic/_helpers";
import { DropdownTopbarItemToggler } from "../../_metronic/_partials/dropdowns";
import { useDispatch } from "react-redux";
import * as actions from "./redux/ComponentsActions";
import { usePlant, useAccessibleProductionSites, useProductionSites } from "./PlantHook";
import { isScheduledLeadTimesEnabled } from "../utils/IsAvailableOnFeatureFlag";

type ProductionSiteData = {
  plantCode: string;
  timeZoneName: string;
  timeZoneLocation: string;
  plantSupportsCarrierConfiguration: boolean;
  currentOffset: string;
  flag: string;
  isActive: boolean;
};

const mapToPlants = (productionSites: any[]): ProductionSiteData[] => {
  let plants = productionSites.map(x => {
    const now = new Date();
    const zonedTime = toZonedTime(now, x.ianaTimeZone);
    const offset = format(zonedTime, 'xxx', { timeZone: x.ianaTimeZone });

    return {
      plantCode: x.code,
      timeZoneLocation: x.ianaTimeZone,
      plantSupportsCarrierConfiguration: x.shippingConfiguration?.carrierOverridesSupported ?? false,
      currentOffset: offset,
      flag: CountryFlags.find(y => y.countryCode === x.countryCode)?.flag,
      isActive: x.isActive
    } as ProductionSiteData;
  });

  return plants.sort((a, b) => a.plantCode.localeCompare(b.plantCode));
}

export function GetAccessiblePlants(): ProductionSiteData[] {
  let productionSites = useAccessibleProductionSites();
  return mapToPlants(productionSites);
}

export function GetAllPlants(): ProductionSiteData[] {
  let productionSites = useProductionSites();
  return mapToPlants(productionSites);
}

export function GetActiveProductionSites(): ProductionSiteData[] {
  const plants = GetAccessiblePlants();
  return plants.filter(x => x.isActive);
}

export function GetAllActiveProductionSites(): ProductionSiteData[] {
  const plants = GetAllPlants();
  return plants.filter(x => x.isActive);
}

export const PlantSelectorDropdown: FC<{ props: any }> = ({
  props,
}): ReactElement => {
  const [currentTime, setCurrentTime] = useState(new Date().toISOString());

  setInterval(() => setCurrentTime(new Date().toISOString()), 60000);

  const currentPlantCode = usePlant();
  const dispatch = useDispatch();
  let plants = GetAccessiblePlants();

  plants = plants.sort((a, b) => a.plantCode.localeCompare(b.plantCode));
  const currentPlant =
    plants.find((x) => x.plantCode === currentPlantCode) ?? plants[0];

  const setPlant = (plantCode: string, plantSupportsCarrierConfiguration: boolean) => {
    dispatch(actions.selectPlant(plantCode, plantSupportsCarrierConfiguration));
  };

  return plants.length < 1 ? (
    <></>
  ) : (
    <Dropdown drop="down" alignRight>
      <Dropdown.Toggle
        as={DropdownTopbarItemToggler}
        id="dropdown-toggle-my-cart"
      >
        <div className="topbar-item">
          <div
            className="btn btn-icon w-auto btn-clean d-flex align-items-center btn-lg px-5"
            id="kt_quick_user_toggle"
          >
            <OverlayTrigger
              placement='bottom'
              overlay={
                <Tooltip id='language-panel-tooltip'>Select Plant</Tooltip>
              }
            >
              <div>
                <div className='d-flex flex-row bd-highlight'>
                  {currentPlant.flag &&
                    <span className='mr-1 pt-2'>
                      <span className='symbol symbol-20 mr-2'>
                        <img
                          className='h-25px w-25px rounded'
                          src={currentPlant.flag}
                          alt={currentPlant.plantCode}
                        />
                      </span>
                    </span>
                  }
                  <span className='navi-text pt-3'>{currentPlant.plantCode}</span>
                  {isScheduledLeadTimesEnabled && currentPlant.timeZoneLocation &&
                    <span className='ml-5 bd-highlight text-left'>
                      <div className='font-small'>Local factory time</div>
                      <div className='font-size-base font-weight-bolder'>{format(toZonedTime(currentTime, currentPlant.timeZoneLocation), 'HH:mm (zzz)', { timeZone: currentPlant.timeZoneLocation })}</div>
                    </span>}
                </div>
              </div>
            </OverlayTrigger>
          </div>
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu className="p-0 m-0 dropdown-menu-left dropdown-menu-anim dropdown-menu-top-unround">
        <ul className="navi navi-hover py-4">
          {plants.map((plant) => (
            <Dropdown.Item
              key={plant.plantCode}
              onClick={() => setPlant(plant.plantCode, plant.plantSupportsCarrierConfiguration ?? false)}
            >
              <li
                key={plant.plantCode}
                className={clsx("navi-item d-flex align-items-center", {
                  active: plant.plantCode === currentPlant.plantCode,
                })}
              >
                {plant.flag &&
                  <span className="symbol symbol-20 mr-3">
                    <img src={plant.flag} alt={plant.plantCode} />
                  </span>
                }
                <span className="navi-text">{plant.plantCode}</span>
              </li>
            </Dropdown.Item>
          ))}
        </ul>
      </Dropdown.Menu>
    </Dropdown>
  );
};
